import type { ReactNode } from "react";
import { ArrowLeft, Settings, UserRoundPen, IdCard, Newspaper, MessageSquarePlus, Rocket } from "lucide-react";
import logo from "public/chatmcp.svg";
import { useRef, useEffect, useState } from "react";
import { ResponsiveLayout } from "./ResponsiveLayout";
import { QuickNav } from "./QuickNav";

interface AppShellProps {
  contactsPanel: ReactNode;
  threadsPanel?: ReactNode;
  chatPanel: ReactNode;
  activePanel: "contacts" | "threads" | "chat";
  onBack?: () => void;
  onOpenProfile?: () => void;
  onOpenAccountTab?: (tab: string) => void;
  onOpenDemo?: () => void;
  onOpenWishlist?: () => void;
  onOpenNewsletter?: () => void;
}

export const AppShell = ({
  contactsPanel,
  threadsPanel,
  chatPanel,
  activePanel,
  onBack,
  onOpenProfile,
  onOpenAccountTab,
  onOpenDemo,
  onOpenWishlist,
  onOpenNewsletter,
}: AppShellProps) => {
  const isChat = activePanel === "chat";
  const isThreads = activePanel === "threads";
  const [mobileView, setMobileView] = useState<"contacts" | "threads">("threads");

  // @note - Settings dropdown closes on outside click and Escape key
  const settingsRef = useRef<HTMLDetailsElement>(null);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (
        settingsRef.current &&
        settingsRef.current.open &&
        !settingsRef.current.contains(e.target as Node)
      ) {
        settingsRef.current.open = false;
      }
    };
    const handleKeyDown = (e: KeyboardEvent) => {
      if (
        settingsRef.current &&
        settingsRef.current.open &&
        e.key === "Escape"
      ) {
        settingsRef.current.open = false;
      }
    };
    document.addEventListener("mousedown", handleClick);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handleClick);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  // Combined panel for left side (contacts or threads based on mobile view or desktop state)
  const leftPanelContent = (
    <div className="flex flex-col h-full">
      {/* Quick Nav for mobile - only show when not in chat */}
      {!isChat && (
        <div className="lg:hidden">
          <QuickNav
            activeView={mobileView}
            onViewChange={setMobileView}
            showContactsTab={!isThreads}
          />
        </div>
      )}
      
      {/* Desktop: Show threads when in threads or chat mode */}
      {/* Mobile: Show based on mobileView */}
      <div className="flex-1 overflow-hidden">
        {/* On desktop, show threads panel when available */}
        <div className="hidden lg:block h-full">
          {threadsPanel || contactsPanel}
        </div>
        
        {/* On mobile, show based on mobile view selection */}
        <div className="lg:hidden h-full">
          {mobileView === "contacts" ? contactsPanel : threadsPanel}
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen px-2 py-3 sm:px-4 sm:py-6 md:px-8">
      <div className="mx-auto flex max-w-7xl flex-col gap-3 sm:gap-6 h-[calc(100vh-3rem)] sm:h-[calc(100vh-6rem)]">
        <header className="flex flex-col gap-2 sm:gap-3 md:flex-row md:items-center md:justify-between flex-shrink-0">
          <div className="flex items-center gap-2 sm:gap-3">
            {isChat || isThreads ? (
              <button
                type="button"
                onClick={onBack}
                className="inline-flex lg:hidden items-center gap-1.5 sm:gap-2 rounded-full border border-border/60 bg-white/70 px-2 py-1 sm:px-3 sm:py-1.5 text-[10px] sm:text-xs font-medium text-muted-foreground shadow-sm transition hover:-translate-x-0.5 hover:border-primary/40 hover:text-primary dark:bg-white/5"
              >
                <ArrowLeft className="h-3 w-3 sm:h-4 sm:w-4" />
                <span className="hidden sm:inline">{isChat ? "Threads" : "Contacts"}</span>
              </button>
            ) : (
              <span className="pill w-fit bg-primary/15 text-[9px] sm:text-xs uppercase tracking-wide px-1.5 sm:px-2">MCP Native</span>
            )}
            <div className="flex items-center gap-1.5 sm:gap-2">
              <a href="/">
                  <img src={logo} alt="ChatMCP Logo" className="h-8 w-8 sm:h-12 sm:w-12 text-white" />
              </a>
              <div className="space-y-0.5 sm:space-y-1">
                <div className="flex items-center gap-2 sm:gap-4">
                  <h1 className="text-gradient text-xl sm:text-3xl md:text-4xl font-semibold">chatMCP</h1>
                  <a href="https://mcp.com.ai" className="hidden sm:inline">
                    <span className="pill font-semibold transition hover:border-primary/40 hover:text-primary shadow-sm text-gradient px-2 text-[10px] sm:text-xs">Part of the HAPI MCP Stack</span>
                  </a>
                </div>
                <p className="hidden md:block text-xs sm:text-sm text-muted-foreground">
                  Chat built directly on the Model Context Protocol (MCP).
                </p>
              </div>
            </div>
          {/* </div> */}
            <div className="flex items-center gap-1 sm:gap-2 text-[10px] sm:text-xs text-muted-foreground ml-auto">
            {activePanel === "contacts" && (
                <div className="hidden sm:flex items-center gap-1 sm:gap-2">
                <span className="pill bg-secondary/60 text-primary px-1.5 py-0.5 sm:px-2 sm:py-1">Free</span>
                <span className="pill bg-secondary/60 px-1.5 py-0.5 sm:px-2 sm:py-1">Local-first</span>
              </div>
            )}
            <button
              type="button"
              onClick={() => onOpenNewsletter?.()}
              className="flex cursor-pointer list-none items-center gap-1 sm:gap-2 rounded-full border border-border/60 bg-white/70 px-2 py-1 sm:px-3 sm:py-1.5 text-[10px] sm:text-xs font-medium text-muted-foreground shadow-sm transition hover:border-primary/40 hover:text-primary dark:bg-white/5"
            >
              <Newspaper className="h-3 w-3 sm:h-4 sm:w-4" />
              <span className="hidden sm:inline">Newsletter</span>
            </button>

            <details ref={settingsRef} className="relative z-10 group">
              <summary className="flex cursor-pointer list-none items-center gap-1 sm:gap-2 rounded-full border border-border/60 bg-white/70 px-2 py-1 sm:px-3 sm:py-1.5 text-[10px] sm:text-xs font-medium text-muted-foreground shadow-sm transition hover:border-primary/40 hover:text-primary dark:bg-white/5">
                <Settings className="h-3 w-3 sm:h-4 sm:w-4" />
                <span className="hidden sm:inline">Settings</span>
              </summary>
              <div className="absolute right-0 mt-2 w-36 sm:w-44 rounded-xl border border-border/60 bg-white/95 p-2 shadow-lg dark:bg-slate-900 z-50">
                <button
                  type="button"
                  className="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-xs sm:text-sm text-foreground hover:bg-muted"
                  onClick={() => onOpenProfile?.()}
                >
                  <UserRoundPen className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                  Profile
                </button>
                <button
                  type="button"
                  className="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-xs sm:text-sm text-foreground hover:bg-muted"
                  onClick={() => onOpenAccountTab?.("account")}
                >
                  <IdCard className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                  Account
                </button>
                <button
                  type="button"
                  className="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-xs sm:text-sm text-foreground hover:bg-muted"
                  onClick={() => onOpenAccountTab?.("llm")}
                >
                  <Settings className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                  LLM
                </button>
                <button
                  type="button"
                  className="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-xs sm:text-sm text-foreground hover:bg-muted"
                  onClick={() => onOpenAccountTab?.("about")}
                >
                  <Newspaper className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                  About
                </button>
                <div className="my-1 border-t border-border/60" />
                <button
                  type="button"
                  className="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-xs sm:text-sm text-foreground hover:bg-muted"
                  onClick={() => onOpenDemo?.()}
                >
                  <Rocket className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                  Request Demo
                </button>
                <button
                  type="button"
                  className="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-xs sm:text-sm text-foreground hover:bg-muted"
                  onClick={() => onOpenWishlist?.()}
                >
                  <MessageSquarePlus className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                  Send Feedback
                </button>
              </div>
            </details>
          </div>
          </div>
        </header>

        {/* Main content area with responsive layout */}
        <div className="flex-1 min-h-0">
          {activePanel === "contacts" ? (
            // Contacts view - full width
            <div className="h-full">{contactsPanel}</div>
          ) : (
            // Threads or Chat view - use responsive layout
            <ResponsiveLayout
              leftPanel={leftPanelContent}
              rightPanel={chatPanel}
              showLeftPanel={isThreads || isChat}
              onBack={onBack}
              isInChatView={isChat}
              onMobileDrawerClose={() => {
                // @note - Mobile drawer closes automatically, no additional action needed
                // This callback is here for future extensibility
              }}
            />
          )}
        </div>
      </div>
    </div>
  );
};
